import { c as _c } from "react/compiler-runtime";
import { FaroCustomCheckbox } from "@components/common/faro-table/faro-custom-checkbox";
import { AttachmentIcon, Card, NO_TRANSLATE_CLASS, Text } from "@flat-ui";
import { Box, Stack, visuallyHidden } from "@mui-wrapper";
import { MarkupsBoardAvatar } from "@pages/project-details/project-markups/board/markups-board-avatar";
import { MarkupsBoardTags } from "@pages/project-details/project-markups/board/markups-board-tags";
import { MarkupDueDateText } from "@pages/project-details/project-markups/mobile/markup-due-date-text";
import { MarkupListItemMenuMobile } from "@pages/project-details/project-markups/mobile/markup-list-item-menu-mobile";
import { MarkupStatus } from "@pages/project-details/project-markups/status/markup-status";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { toggleItemSelection } from "@utils/array-utils";

/** Width of the menu button, and of the checkbox replacing it while selecting */
const ACTION_WIDTH = "1.75rem";

/**
 * Lifts a part of the card above the card's button, like flat-ui does for its slots, so its own
 * controls, e.g. a tooltip, can be reached. A tap on it is then passed on to the card by hand.
 */
const ABOVE_CARD_BUTTON = {
  position: "relative",
  zIndex: 1,
  cursor: "pointer"
};
/** Paperclip with the number of attachments of a markup */
function MarkupAttachmentsCount(t0) {
  const $ = _c(7);
  const {
    count
  } = t0;
  const t1 = `${count} ${count === 1 ? "attachment" : "attachments"}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: 0.5,
      color: "text.secondary"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AttachmentIcon sx={{
      width: "1rem",
      height: "1rem"
    }} />;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== count) {
    t4 = <Text variant="bodyS" component="var" color="inherit">{count}</Text>;
    $[2] = count;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t1 || $[5] !== t4) {
    t5 = <Stack direction="row" aria-label={t1} sx={t2}>{t3}{t4}</Stack>;
    $[4] = t1;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
/**
 * Card of a markup in the list on small screens, a flat-ui card like the other lists: its name
 * with the attachments and menu on top, then its tags, and the status and due date with the
 * assignee at the bottom. While any markup is selected, the menu turns into a checkbox and a
 * click selects the markup instead of opening it.
 */
export function MarkupListItemCardMobile(t0) {
  const $ = _c(50);
  const {
    markup,
    assignee,
    dueDateUrgency,
    shouldShowStatus,
    isSelected,
    onClick
  } = t0;
  const dispatch = useAppDispatch();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const isSelecting = selectedRowIds.length > 0;
  let t1;
  if ($[0] !== markup.id || $[1] !== selectedRowIds) {
    t1 = selectedRowIds.includes(markup.id);
    $[0] = markup.id;
    $[1] = selectedRowIds;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isChecked = t1;
  const attachmentsCount = markup.attachmentsCount ?? 0;
  let t2;
  if ($[3] !== dispatch || $[4] !== isSelecting || $[5] !== markup || $[6] !== onClick || $[7] !== selectedRowIds) {
    t2 = function handleClick() {
      if (isSelecting) {
        dispatch(setInitialFetchingItems(toggleItemSelection(selectedRowIds, markup.id)));
      } else {
        onClick(markup);
      }
    };
    $[3] = dispatch;
    $[4] = isSelecting;
    $[5] = markup;
    $[6] = onClick;
    $[7] = selectedRowIds;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleClick = t2;
  const t3 = isSelected || isChecked;
  let t4;
  if ($[9] !== markup.name) {
    t4 = <span className={NO_TRANSLATE_CLASS}>{markup.name}</span>;
    $[9] = markup.name;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isSelected) {
    t5 = isSelected && <span style={visuallyHidden}>, open</span>;
    $[11] = isSelected;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      alignItems: "center",
      gap: 1
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== attachmentsCount || $[18] !== handleClick) {
    t8 = attachmentsCount > 0 && <Box onClick={handleClick} sx={{
      cursor: "pointer"
    }}><MarkupAttachmentsCount count={attachmentsCount} /></Box>;
    $[17] = attachmentsCount;
    $[18] = handleClick;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== handleClick || $[21] !== isChecked || $[22] !== isSelecting || $[23] !== markup) {
    t9 = isSelecting ? <FaroCustomCheckbox checked={isChecked} onChange={handleClick} controlSx={{
      visibility: "visible"
    }} /> : <MarkupListItemMenuMobile markup={markup} />;
    $[20] = handleClick;
    $[21] = isChecked;
    $[22] = isSelecting;
    $[23] = markup;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <Stack direction="row" sx={t7}>{t8}{t9}</Stack>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== dueDateUrgency || $[29] !== handleClick || $[30] !== markup || $[31] !== shouldShowStatus) {
    t11 = (shouldShowStatus || markup.dueDate) && <Stack direction="row" onClick={handleClick} sx={{
      alignItems: "center",
      gap: 1.5,
      ...ABOVE_CARD_BUTTON
    }}>{shouldShowStatus && <MarkupStatus markup={markup} />}<MarkupDueDateText markup={markup} urgency={dueDateUrgency} /></Stack>;
    $[28] = dueDateUrgency;
    $[29] = handleClick;
    $[30] = markup;
    $[31] = shouldShowStatus;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== assignee || $[34] !== handleClick) {
    t12 = assignee && <Box onClick={handleClick} sx={{
      display: "flex",
      justifyContent: "center",
      width: ACTION_WIDTH,
      cursor: "pointer"
    }}><MarkupsBoardAvatar assignee={assignee} /></Box>;
    $[33] = assignee;
    $[34] = handleClick;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== handleClick || $[37] !== markup.labels) {
    t13 = !!markup.labels?.length && <Box onClick={handleClick} sx={{
      m: -1,
      p: 1,
      cursor: "pointer"
    }}><MarkupsBoardTags tags={markup.labels} maxVisibleTags={3} /></Box>;
    $[36] = handleClick;
    $[37] = markup.labels;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== handleClick || $[40] !== t10 || $[41] !== t11 || $[42] !== t12 || $[43] !== t13 || $[44] !== t3 || $[45] !== t6) {
    t14 = <Card variant="standard" selected={t3} onClick={handleClick} title={t6} headerAction={t10} footer={t11} actionsVisibility="always" actions={t12}>{t13}</Card>;
    $[39] = handleClick;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
    $[44] = t3;
    $[45] = t6;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== markup.id || $[48] !== t14) {
    t15 = <Box data-markup-id={markup.id}>{t14}</Box>;
    $[47] = markup.id;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
}